/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { jsx } from '@compiled/react';
import { type NativeImageProps, type RenderFn } from '@atlassian/forge-ui-types';
import { useIsValidImageUrl } from '../../utils';
import { EgressErrorMessage } from '../..';

export const maxWidths = {
	xsmall: '25%',
	small: '37.5%',
	medium: '50%',
	large: '75%',
	xlarge: '100%',
};

function ImageInner({ forgeDoc }: Parameters<RenderFn>[0]) {
	const { src, alt, size = 'xlarge', width, height } = forgeDoc.props as NativeImageProps;
	const isCustomSize = width || height;

	const fixedImageSize = maxWidths[size] ? maxWidths[size] : '100%';
	const maxWidth = isCustomSize ? '100%' : fixedImageSize; // the width and height props will override the size prop

	return (
		<img
			style={{
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				display: 'block',
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				margin: 'auto',
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				maxWidth,
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
				textAlign: 'center',
			}}
			src={src}
			alt={alt}
			width={width}
			height={height}
		/>
	);
}

const ImageWithSrcValidation = (props: Parameters<RenderFn>[0]) => {
	const { src } = props.forgeDoc.props as NativeImageProps;

	const isValidUrl = useIsValidImageUrl(src);

	if (isValidUrl) {
		return <ImageInner {...props} />;
	}

	return <EgressErrorMessage url={src} />;
};

export const Image = (props: Parameters<RenderFn>[0]) => {
	const { src } = props.forgeDoc.props as NativeImageProps;
	const isAbsolutePath = src.startsWith('/');

	// If `src` is an absolute path, we can render the image immediately.
	// The resolved path will be routed to the tenant domain which is already authorised in our egress service so no validation is required.
	// This is required as Confluence endpoints tend to return absolute paths for avatar images.
	if (isAbsolutePath) {
		return <ImageInner {...props} />;
	}

	return <ImageWithSrcValidation {...props} />;
};
